import { Component, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class AuthErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Auth Error Boundary caught error:', error, errorInfo);
    
    // Check if it's a Clerk-related error
    if (error.message?.includes('Clerk') || error.message?.includes('VITE_CLERK_PUBLISHABLE_KEY')) {
      console.error('Clerk authentication initialization failed:', error);
    }
  }

  render() {
    if (this.state.hasError) {
      const isClerkError = this.state.error?.message?.includes('Clerk') || 
                          this.state.error?.message?.includes('VITE_CLERK_PUBLISHABLE_KEY');

      return (
        <div className="min-h-screen flex items-center justify-center bg-background">
          <div className="max-w-md mx-auto p-8 bg-card rounded-lg shadow-lg text-center">
            <h1 className="text-2xl font-bold text-foreground mb-4">
              {isClerkError ? 'Authentication Error' : 'Something went wrong'}
            </h1>
            <p className="text-muted-foreground mb-6">
              {isClerkError 
                ? 'There was a problem initializing authentication. Please check your configuration and try again.'
                : 'An unexpected error occurred. Please refresh the page to try again.'
              }
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Refresh Page
            </button>
            {import.meta.env.DEV && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 p-4 bg-muted rounded-md text-xs overflow-auto">
                  {this.state.error?.stack || this.state.error?.message}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AuthErrorBoundary;